import { produce } from "immer";
import { ActionType, createReducer } from "typesafe-actions";
import { LEADERBOARD_BOARD, LEADERBOARD_PERIOD } from "@shared/models";

import { SparksStateType } from "../../interfaces";
import * as actions from "./actions";

type Action = ActionType<typeof actions>;

export const initialState: SparksStateType = {
  summary: null,
  summaryIsLoaded: false,
  summaryLoadFailed: false,
  badges: null,
  badgesIsLoaded: false,
  badgesLoadFailed: false,
  badgeDefinitions: null,
  badgeDefinitionsIsLoaded: false,
  badgeDefinitionsLoadFailed: false,
  givingActivity: null,
  givingActivityIsLoaded: false,
  givingActivityLoadFailed: false,
  profileBadges: null,
  leaderboardResults: [],
  leaderboardResultsIsLoaded: false,
  leaderboardResultsLoadFailed: false,
  // L2 (923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md): see these fields' own interface comments for their
  // lifecycle. The two `leaderboardResults*` toggles below start `null` -- nothing has been fetched,
  // so there is no board/period the retained (empty) results describe, and the container's freshness
  // check must therefore be false on the first frame rather than accidentally true.
  leaderboardViewer: null,
  leaderboardResultsBoard: null,
  leaderboardResultsPeriod: null,
  // L7: starts `null` alongside its group -- nothing has been fetched, so there is no window yet.
  leaderboardResultsWindow: null,
  leaderboardCrowns: [],
  leaderboardCrownsIsLoaded: false,
  leaderboardCrownsLoadFailed: false,
  leaderboardBoard: LEADERBOARD_BOARD.RECEIVED,
  // leaderboard-default-quarter: the Leaderboard tab's own default -- Figma reads quarterly throughout
  // ("This quarter", "Resets in 18 days · new quarter 1 Oct", "Best quarter"). Kept in step with
  // clearLeaderboard's own reset below (that action re-applies this exact default on the tab's
  // unmount) -- see that assignment's own comment.
  leaderboardPeriod: LEADERBOARD_PERIOD.QUARTER,
  overviewLeaderboardResults: null,
  overviewLeaderboardIsLoaded: false,
  overviewLeaderboardLoadFailed: false,
  ownRedemptionSummary: null,
  ownRedemptionSummaryIsLoaded: false,
  ownRedemptionSummaryLoadFailed: false,
  rewardItems: null,
  rewardItemsIsLoaded: false,
  rewardItemsLoadFailed: false,
  pendingRedemptionRequests: [],
  pendingRedemptionRequestsTotal: 0,
  pendingRedemptionRequestsIsLoaded: false,
  pendingRedemptionRequestsLoadFailed: false,
  pendingRedemptionRequestsLoadedPages: 0,
  // 🚨 Rule 1 (923-core-context context/engineering/spark-rewards/phase-49-w3-pending-requests-panel.md): starts `true`, not `false` -- `false` would make
  // the container's `lastPageWasFull && loadedPages * limit < total` guard false from mount, so the
  // FIRST page would never be requested at all, and an empty queue would look identical to a full one.
  pendingRedemptionRequestsLastPageWasFull: true,
  // fix pass G: 0 until the first success persists a real (positive) limit -- see this field's own
  // interface comment.
  pendingRedemptionRequestsLimit: 0,
  // 923-core-context context/engineering/spark-rewards/phase-49-w4-approve-deny.md: the in-flight set for per-row approve/deny writes -- see this field's
  // own interface comment for why it is a plain array, not a real Set.
  redemptionRequestDecisionInFlightIds: [],
  // 923-core-context context/engineering/spark-rewards/phase-49-w4-approve-deny.md: the tab badge's own count -- see this field's own interface comment
  // for why it is deliberately excluded from clearSparks's reset below.
  pendingRedemptionRequestsBadgeCount: 0,
  // 923-core-context context/engineering/spark-rewards/phase-49-w5-org-wide-ledger.md: the org-wide ledger's own pager -- see this field's own interface
  // comment for why it mirrors pendingRedemptionRequests* field-for-field.
  redemptionLedgerRows: [],
  redemptionLedgerTotal: 0,
  redemptionLedgerIsLoaded: false,
  redemptionLedgerLoadFailed: false,
  redemptionLedgerLoadedPages: 0,
  redemptionLedgerLastPageWasFull: true,
  redemptionLedgerLimit: 0,
  // phase-50-w6b: the three org-wide stat cards -- mirrors rewardItems' own trio field-for-field (org-wide,
  // not scoped to any one person).
  redemptionStats: null,
  redemptionStatsIsLoaded: false,
  redemptionStatsLoadFailed: false,
  // 923-core-context context/engineering/spark-rewards/phase-52-spread-your-sparks.md §4 S3: "Spread your Sparks" -- mirrors rewardItems' own trio
  // field-for-field (viewer-scoped, reset alongside the rest of this logged-in user's own Sparks
  // lifecycle on clearSparks below).
  leaderboardCollaborators: null,
  leaderboardCollaboratorsIsLoaded: false,
  leaderboardCollaboratorsLoadFailed: false,
  // 923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md L8: the "Your all-time" card's own two pairs -- see
  // SparksState.interface.tsx's own comment for why these reset on clearSparks, not clearLeaderboard.
  // Review follow-up: no `*IsLoaded` field here (unlike this file's other trios) -- the card's own
  // failure fix (LeaderboardsContainer.tsx) swaps the whole card out on `*LoadFailed` alone, the same
  // way `coreValuesLoadFailed` already does for the value chips row, so no consumer ever needed one.
  allTimeRank: null,
  allTimeRankLoadFailed: false,
  allTimeSummary: null,
  allTimeSummaryLoadFailed: false,
  // 923-core-context context/engineering/spark-rewards/phase-55-person-sparks-drawer.md §6: the drawer's own person-keyed slot -- closed (`userId: null`)
  // until a ledger row or a pending-request name is clicked. See SparksStateType's own comment.
  personSparks: {
    userId: null,
    data: null,
    loading: false,
    failed: false,
  },
};

const reducer = createReducer<SparksStateType, Action>(initialState)
  .handleAction(actions.getSparksSummary.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.summary = action.payload;
      nextState.summaryIsLoaded = true;
      // A retry after a failure clears the message instead of showing both stale error and result.
      nextState.summaryLoadFailed = false;
    }),
  )
  .handleAction(actions.getSparksSummary.failure, (state) =>
    produce(state, (nextState) => {
      // Deliberately does not clear `summary` — a failure that follows an earlier success keeps
      // the stale totals on screen alongside the failure message. Hiding them would read as an
      // empty state rather than an error, which is worse.
      nextState.summaryLoadFailed = true;
    }),
  )
  /**
   * Same treatment `getRedemptionLedger.request` already gets below, and for the same reason: clear a
   * stale failure flag the instant a NEW fetch starts, rather than waiting for that fetch's own
   * `.success`. Without it the flag survives across an unmount/remount -- badges fail on the dedicated
   * Badges screen, navigate away and back, and the very first render shows "Couldn't load your badge
   * progress." before the replacement request has even been dispatched, describing a fetch that is not
   * in flight. `clearSparks` closes the same hole only for consumers that dispatch it; this closes it
   * for every consumer, including one that never unmounts.
   *
   * Deliberately does NOT clear `badges`/`badgeDefinitions` themselves: the "keep last-known-good data
   * on screen under a failure note" rule the `.failure` handlers below state applies just as much to
   * an in-flight refetch, and blanking the data here would flash the screen empty on every revisit.
   */
  .handleAction(actions.getUserBadges.request, (state) =>
    produce(state, (nextState) => {
      nextState.badgesLoadFailed = false;
    }),
  )
  .handleAction(actions.getBadgeDefinitions.request, (state) =>
    produce(state, (nextState) => {
      nextState.badgeDefinitionsLoadFailed = false;
    }),
  )
  .handleAction(actions.getUserBadges.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.badges = action.payload;
      nextState.badgesIsLoaded = true;
      // A retry after a failure clears the message instead of showing both stale error and result.
      nextState.badgesLoadFailed = false;
    }),
  )
  .handleAction(actions.getUserBadges.failure, (state) =>
    produce(state, (nextState) => {
      // Deliberately does not clear `badges` — a failure that follows an earlier success keeps
      // the stale data on screen alongside the failure message. Hiding them would read as an
      // empty state rather than an error, which is worse.
      nextState.badgesLoadFailed = true;
    }),
  )
  .handleAction(actions.getBadgeDefinitions.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.badgeDefinitions = action.payload;
      nextState.badgeDefinitionsIsLoaded = true;
      // A retry after a failure clears the message instead of showing both stale error and result.
      nextState.badgeDefinitionsLoadFailed = false;
    }),
  )
  .handleAction(actions.getBadgeDefinitions.failure, (state) =>
    produce(state, (nextState) => {
      // Deliberately does not clear `badgeDefinitions` — a failure that follows an earlier success
      // keeps the stale catalog on screen alongside the failure message. Hiding it would read as an
      // empty state rather than an error, which is worse.
      nextState.badgeDefinitionsLoadFailed = true;
    }),
  )
  // overview-rebuild-pr5-plan.md: mirrors getBadgeDefinitions' pair exactly -- `.success` sets the
  // data and clears a prior failure flag; `.failure` sets ONLY the flag, deliberately leaving stale
  // data on screen (same "don't hide last-known-good numbers" rule as summary/badges/badgeDefinitions).
  .handleAction(actions.getGivingActivity.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.givingActivity = action.payload;
      nextState.givingActivityIsLoaded = true;
      nextState.givingActivityLoadFailed = false;
    }),
  )
  .handleAction(actions.getGivingActivity.failure, (state) =>
    produce(state, (nextState) => {
      nextState.givingActivityLoadFailed = true;
    }),
  )
  // 923-core-context context/engineering/spark-rewards/phase-49-w1-rewards-catalog.md: mirrors getBadgeDefinitions' pair exactly -- `.success` sets the
  // data and clears a prior failure flag; `.failure` sets ONLY the flag, deliberately leaving stale
  // data on screen (same "don't hide last-known-good numbers" rule as summary/badges/badgeDefinitions).
  .handleAction(actions.getRewardItems.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.rewardItems = action.payload;
      nextState.rewardItemsIsLoaded = true;
      nextState.rewardItemsLoadFailed = false;
    }),
  )
  .handleAction(actions.getRewardItems.failure, (state) =>
    produce(state, (nextState) => {
      nextState.rewardItemsLoadFailed = true;
    }),
  )
  /**
   * 923-core-context context/engineering/spark-rewards/phase-49-w3-pending-requests-panel.md: the HR queue's own append pager, copying
   * UserRecognitionsContainer/Chat's precedent (is_clear replace-vs-append) plus the three rules a
   * reviewer specifically flagged as most likely to be paraphrased into a plain increment:
   *
   * 1. `pendingRedemptionRequestsLoadedPages` is ASSIGNED `action.payload.page + 1`, never incremented
   *    (`nextState... + 1`) and never derived from `results.length` -- neither alternative survives a
   *    mid-session page-0 replace (a future W4 refresh policy): an increment would leave the counter
   *    past what a fresh replace actually holds, and a row-count derivation would re-request the same
   *    page forever the moment a page returns fewer rows than `limit`.
   * 2. `pendingRedemptionRequestsLastPageWasFull` is written on EVERY success, the replace branch
   *    included -- a short final page's `false` must not survive into a later page-0 replace that
   *    happens to return a full page.
   * 3. Only THIS handler writes `pendingRedemptionRequestsLoadedPages`/`LastPageWasFull` -- `.failure`
   *    below touches neither, so a failed page stays retryable (the next scroll dispatches the SAME
   *    page again) and is never mistaken for a short/terminal page.
   *
   * fix pass F: the append branch de-dupes by `id`, mirroring `Recognition/store/reducer.tsx`'s own
   * `getReceivedRecognitions`/`getSentRecognitions`/`getReviewRecognitions` precedent (a `Set` of
   * already-held ids, filtering the incoming page against it) rather than inventing a new mechanism --
   * this queue is OFFSET-paged over rows other HR users are concurrently approving/denying, so a row
   * already held can shift onto a later page and arrive twice. `Table` keys rows by array index
   * (`shared/components/Table/Table.tsx`), so an un-de-duped repeat would never even surface as a
   * duplicate-key warning -- it would just silently render the same request twice.
   *
   * fix pass G: `pendingRedemptionRequestsLimit` is persisted from THIS response's own `limit` on every
   * success, the replace branch included -- see that field's own interface comment for why the
   * terminator must read it back rather than a container-level constant.
   */
  .handleAction(actions.getPendingRedemptionRequests.success, (state, action) =>
    produce(state, (nextState) => {
      const { results, total, is_clear, page, limit } = action.payload;
      if (is_clear) {
        nextState.pendingRedemptionRequests = [...results];
      } else {
        const existingIds = new Set(nextState.pendingRedemptionRequests.map((row) => row.id));
        nextState.pendingRedemptionRequests = [
          ...nextState.pendingRedemptionRequests,
          ...results.filter((row) => !existingIds.has(row.id)),
        ];
      }
      // 🚨 `total` has exactly one writer: this response. Whatever `{ results, total }` arrives last
      // wins -- never adjusted by arithmetic anywhere else in this slice.
      nextState.pendingRedemptionRequestsTotal = total;
      nextState.pendingRedemptionRequestsIsLoaded = true;
      nextState.pendingRedemptionRequestsLoadFailed = false;
      nextState.pendingRedemptionRequestsLoadedPages = page + 1;
      nextState.pendingRedemptionRequestsLastPageWasFull = results.length === limit;
      nextState.pendingRedemptionRequestsLimit = limit;
    }),
  )
  .handleAction(actions.getPendingRedemptionRequests.failure, (state) =>
    produce(state, (nextState) => {
      // Deliberately does not touch pendingRedemptionRequestsLoadedPages/LastPageWasFull -- see rule 3
      // in this handler pair's own comment above.
      nextState.pendingRedemptionRequestsLoadFailed = true;
    }),
  )
  /**
   * 923-core-context context/engineering/spark-rewards/phase-49-w5-org-wide-ledger.md: the org-wide ledger's own append pager -- FIELD-FOR-FIELD the same
   * six rules as getPendingRedemptionRequests.success above (architecture §4.6 applies verbatim, and
   * [decision] copies the vertical slice rather than extracting a shared reducer helper). `is_clear`
   * REPLACES (the initial org-wide load on mount, or an explicit post-decision refresh); anything else
   * APPENDS and de-dupes by `id`, since this list is offset-paged and can shift rows between fetches the
   * same way the HR queue can.
   */
  /**
   * fix E: clears a stale `redemptionLedgerLoadFailed` the instant a new fetch starts, on `.request`
   * rather than waiting for `.success` -- without this, the inline "couldn't load the next page" banner
   * (keyed off `redemptionLedgerLoadFailed`) can survive a retry and describe a fetch that is no longer
   * in flight for the whole round trip of the very request meant to clear it.
   */
  .handleAction(actions.getRedemptionLedger.request, (state) =>
    produce(state, (nextState) => {
      nextState.redemptionLedgerLoadFailed = false;
    }),
  )
  .handleAction(actions.getRedemptionLedger.success, (state, action) =>
    produce(state, (nextState) => {
      const { results, total, is_clear, page, limit } = action.payload;
      if (is_clear) {
        nextState.redemptionLedgerRows = [...results];
      } else {
        const existingIds = new Set(nextState.redemptionLedgerRows.map((row) => row.id));
        nextState.redemptionLedgerRows = [
          ...nextState.redemptionLedgerRows,
          ...results.filter((row) => !existingIds.has(row.id)),
        ];
      }
      // 🚨 `total` has exactly one writer: this response.
      nextState.redemptionLedgerTotal = total;
      nextState.redemptionLedgerIsLoaded = true;
      nextState.redemptionLedgerLoadFailed = false;
      nextState.redemptionLedgerLoadedPages = page + 1;
      nextState.redemptionLedgerLastPageWasFull = results.length === limit;
      nextState.redemptionLedgerLimit = limit;
    }),
  )
  .handleAction(actions.getRedemptionLedger.failure, (state) =>
    produce(state, (nextState) => {
      // Deliberately does not touch redemptionLedgerLoadedPages/LastPageWasFull -- rule 3, same as
      // getPendingRedemptionRequests's own failure handler above: a failed page must stay retryable.
      nextState.redemptionLedgerLoadFailed = true;
    }),
  )
  /**
   * review fix A-C: the ledger's own dedicated reset, for the PAGER fields.
   * RedemptionsContainer's mount effect dispatches this UNCONDITIONALLY, immediately before its own
   * page-0 `getRedemptionLedger.request` -- so by the time that request's response (success OR
   * failure) lands, every pager field is already at these reset values rather than whatever a previous
   * mount had left behind. `.failure` deliberately never touches `redemptionLedgerLoadedPages`/`Total`/
   * `Rows` (rule 3), so without a reset that runs ahead of the request, a retry after a remount could
   * otherwise replay a stale page number or total left over from before.
   */
  .handleAction(actions.clearRedemptionLedger, (state) =>
    produce(state, (nextState) => {
      nextState.redemptionLedgerRows = [];
      nextState.redemptionLedgerTotal = 0;
      nextState.redemptionLedgerIsLoaded = false;
      nextState.redemptionLedgerLoadFailed = false;
      nextState.redemptionLedgerLoadedPages = 0;
      nextState.redemptionLedgerLastPageWasFull = true;
      nextState.redemptionLedgerLimit = 0;
    }),
  )
  /**
   * 923-core-context context/engineering/spark-rewards/phase-49-w4-approve-deny.md requirement 1: an id is added to the in-flight set on `.request` --
   * copy-on-write mechanics mirroring ReviewQueueContainer's own `new Set(prev)` idiom
   * (ReviewQueueContainer.tsx:237-253), translated to a plain array since this field lives in redux
   * (see the field's own interface comment for why it isn't a real Set). Guarded with `includes` so a
   * duplicate `.request` for an already-in-flight id -- which the button's own `disabled` prop should
   * already prevent -- cannot push the same id twice.
   */
  .handleAction(actions.approveRedemptionRequest.request, (state, action) =>
    produce(state, (nextState) => {
      if (!nextState.redemptionRequestDecisionInFlightIds.includes(action.payload.id)) {
        nextState.redemptionRequestDecisionInFlightIds.push(action.payload.id);
      }
    }),
  )
  /**
   * [decision] (923-core-context context/engineering/spark-rewards/phase-49-w4-approve-deny.md): remove the decided row for immediate feedback and clear
   * its in-flight flag. The page-0 refetch that restores `total`'s authority is a SEPARATE dispatch
   * (the container's own `cb`, fired after this handler has already run), never client arithmetic on
   * `total` itself -- architecture §4.6 rule 4. This handler touches NEITHER
   * `pendingRedemptionRequestsTotal` NOR any other pager field (`…LoadedPages`/`…LastPageWasFull`/
   * `…Limit`) -- only a `getPendingRedemptionRequests` response may write those (see the "total is
   * never decremented client-side" structural test in reducer.test.tsx, which asserts this by counting
   * assignment sites).
   */
  .handleAction(actions.approveRedemptionRequest.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.redemptionRequestDecisionInFlightIds = nextState.redemptionRequestDecisionInFlightIds.filter(
        (id) => id !== action.payload.request.id,
      );
      nextState.pendingRedemptionRequests = nextState.pendingRedemptionRequests.filter(
        (row) => row.id !== action.payload.request.id,
      );
    }),
  )
  /**
   * Requirement 1's other half: `.failure` ALSO removes the id -- an id left behind after a rejected
   * write would disable that row permanently, with nothing in the UI to explain why. Does NOT touch
   * `pendingRedemptionRequests` -- a failed decision leaves the row exactly where it was.
   */
  .handleAction(actions.approveRedemptionRequest.failure, (state, action) =>
    produce(state, (nextState) => {
      nextState.redemptionRequestDecisionInFlightIds = nextState.redemptionRequestDecisionInFlightIds.filter(
        (id) => id !== action.payload.id,
      );
    }),
  )
  // denyRedemptionRequest's own trio -- mirrors approveRedemptionRequest's three handlers exactly,
  // field-for-field. A SEPARATE action type from approve, not a shared handler discriminating on
  // decision kind, so both can independently add to/remove from the SAME shared in-flight set without
  // one registration silently replacing the other's -- typesafe-actions merges registrations for the
  // SAME action type with Object.assign (this file's own warning below), which does not apply here
  // since approve and deny are two different types.
  .handleAction(actions.denyRedemptionRequest.request, (state, action) =>
    produce(state, (nextState) => {
      if (!nextState.redemptionRequestDecisionInFlightIds.includes(action.payload.id)) {
        nextState.redemptionRequestDecisionInFlightIds.push(action.payload.id);
      }
    }),
  )
  .handleAction(actions.denyRedemptionRequest.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.redemptionRequestDecisionInFlightIds = nextState.redemptionRequestDecisionInFlightIds.filter(
        (id) => id !== action.payload.request.id,
      );
      nextState.pendingRedemptionRequests = nextState.pendingRedemptionRequests.filter(
        (row) => row.id !== action.payload.request.id,
      );
    }),
  )
  .handleAction(actions.denyRedemptionRequest.failure, (state, action) =>
    produce(state, (nextState) => {
      nextState.redemptionRequestDecisionInFlightIds = nextState.redemptionRequestDecisionInFlightIds.filter(
        (id) => id !== action.payload.id,
      );
    }),
  )
  /**
   * 923-core-context context/engineering/spark-rewards/phase-49-w4-approve-deny.md: the tab badge's OWN poll -- writes ONLY this field. Must never touch
   * `pendingRedemptionRequests`/`…Total`/`…Limit`/`…LoadedPages`/`…LastPageWasFull`; see this action's
   * own actions.tsx comment for the pager-destroying alternative this avoids. No `.failure` handler --
   * mirrors Recognition/store/reducer.tsx's identical `getPendingCount.success`-only precedent: a
   * failed background poll leaves the last-known-good count on screen rather than blanking it or
   * surfacing an error for a fetch nobody asked for.
   */
  .handleAction(actions.getPendingRedemptionRequestsBadgeCount.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.pendingRedemptionRequestsBadgeCount = action.payload;
    }),
  )
  .handleAction(actions.clearSparks, (state) =>
    produce(state, (nextState) => {
      nextState.summary = null;
      nextState.summaryIsLoaded = false;
      nextState.summaryLoadFailed = false;
      nextState.badges = null;
      nextState.badgesIsLoaded = false;
      nextState.badgesLoadFailed = false;
      nextState.badgeDefinitions = null;
      nextState.badgeDefinitionsIsLoaded = false;
      nextState.badgeDefinitionsLoadFailed = false;
      // overview-rebuild-pr5-plan.md: givingActivity is self-only, the same logged-in user's OWN
      // lifecycle as summary/badges/badgeDefinitions above -- omitting it here would leave the
      // previous person's giving week on screen after a user switch or a remount, until the next
      // fetch resolves.
      nextState.givingActivity = null;
      nextState.givingActivityIsLoaded = false;
      nextState.givingActivityLoadFailed = false;
      // overview-leaderboard-card-plan.md Part 2: the Overview rail card's own trio resets here, NOT
      // on `clearLeaderboard` (the tab's own reset) -- this card's data must survive the Leaderboard
      // tab being mounted/unmounted, and must only clear on the SAME lifecycle as the rest of this
      // logged-in user's own Overview data (summary/badges/badgeDefinitions/givingActivity above).
      nextState.overviewLeaderboardResults = null;
      nextState.overviewLeaderboardIsLoaded = false;
      nextState.overviewLeaderboardLoadFailed = false;
      // redeem-tab-plan.md PR2: ownRedemptionSummary is the Redeem tab's read of this SAME logged-in
      // user, so it resets here alongside summary/badges/badgeDefinitions.
      nextState.ownRedemptionSummary = null;
      nextState.ownRedemptionSummaryIsLoaded = false;
      nextState.ownRedemptionSummaryLoadFailed = false;
      // 923-core-context context/engineering/spark-rewards/phase-49-w1-rewards-catalog.md: rewardItems resets here alongside summary/badges/
      // badgeDefinitions purely for remount-freshness/uniformity with the rest of this reset -- NOT
      // because the catalog belongs to the logged-in user's own lifecycle: it is USER-INDEPENDENT (see
      // actions.tsx's own comment above getRewardItems and SparksActionTypes.GET_REWARD_ITEMS's own
      // comment). Resetting it here just means a stale catalog doesn't survive a remount, mirroring
      // ownRedemptionSummary's own comment just above.
      nextState.rewardItems = null;
      nextState.rewardItemsIsLoaded = false;
      nextState.rewardItemsLoadFailed = false;
      // 923-core-context context/engineering/spark-rewards/phase-49-w3-pending-requests-panel.md [decision]: the HR queue resets here, on the whole
      // Sparks slice's own remount lifecycle, since it is org-wide and not scoped to any one person.
      nextState.pendingRedemptionRequests = [];
      nextState.pendingRedemptionRequestsTotal = 0;
      nextState.pendingRedemptionRequestsIsLoaded = false;
      nextState.pendingRedemptionRequestsLoadFailed = false;
      nextState.pendingRedemptionRequestsLoadedPages = 0;
      nextState.pendingRedemptionRequestsLastPageWasFull = true;
      nextState.pendingRedemptionRequestsLimit = 0;
      // 923-core-context context/engineering/spark-rewards/phase-49-w4-approve-deny.md: reset alongside the queue above -- an in-flight id only means
      // anything relative to the rows this same reset just cleared. Deliberately does NOT reset
      // `pendingRedemptionRequestsBadgeCount` -- see that field's own interface comment for why.
      nextState.redemptionRequestDecisionInFlightIds = [];
      // 923-core-context context/engineering/spark-rewards/phase-49-w5-org-wide-ledger.md: the org-wide ledger resets here, on the same whole-slice
      // remount lifecycle as the queue above -- it is org-wide, not scoped to any one person.
      nextState.redemptionLedgerRows = [];
      nextState.redemptionLedgerTotal = 0;
      nextState.redemptionLedgerIsLoaded = false;
      nextState.redemptionLedgerLoadFailed = false;
      nextState.redemptionLedgerLoadedPages = 0;
      nextState.redemptionLedgerLastPageWasFull = true;
      nextState.redemptionLedgerLimit = 0;
      // phase-50-w6b: resets here alongside rewardItems above, for the same remount-freshness/uniformity
      // reason (this data is org-wide/user-independent, not part of the logged-in user's own lifecycle).
      nextState.redemptionStats = null;
      nextState.redemptionStatsIsLoaded = false;
      nextState.redemptionStatsLoadFailed = false;
      // 923-core-context context/engineering/spark-rewards/phase-52-spread-your-sparks.md §4 S3: resets here alongside rewardItems above, same "not the
      // logged-in user's own lifecycle, resets for remount-freshness/uniformity" reasoning -- the
      // section is viewer-scoped by the token, not tied to any one tab's own mount/unmount.
      nextState.leaderboardCollaborators = null;
      nextState.leaderboardCollaboratorsIsLoaded = false;
      nextState.leaderboardCollaboratorsLoadFailed = false;
      // 923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md L8: resets here, alongside leaderboardCollaborators above --
      // same "viewer-scoped, period-independent, not tied to this tab's own mount/unmount" reasoning
      // (SparksState.interface.tsx's own comment on these fields).
      nextState.allTimeRank = null;
      nextState.allTimeRankLoadFailed = false;
      nextState.allTimeSummary = null;
      nextState.allTimeSummaryLoadFailed = false;
      // Phase 37 D2: `profileBadges` is deliberately NOT touched here — it's a viewed profile's own
      // lifecycle, independent of the logged-in user's Sparks slice this action clears.
    }),
  )
  // Phase 37 D2: tags the success payload's `userId` alongside its `badges` — this is the field
  // UserRecognitionsContainer's stale-subject guard depends on, so both parts of the payload are
  // stored verbatim, not just `badges`.
  .handleAction(actions.getProfileBadges.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.profileBadges = action.payload;
    }),
  )
  .handleAction(actions.getProfileBadges.failure, (state) =>
    produce(state, (nextState) => {
      nextState.profileBadges = null;
    }),
  )
  .handleAction(actions.clearProfileBadges, (state) =>
    produce(state, (nextState) => {
      nextState.profileBadges = null;
    }),
  )
  // Phase 38 D5/D6: `getLeaderboard` (received/given) and `getLeaderboardCrowns` are two independent
  // handlers, each with its own loading/failure pair -- never one handler discriminating on `board`,
  // and never a shared pair (a crowns-only failure must not raise the ranked board's error state, and
  // vice versa).
  // L2 (923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md): writes `leaderboardResults`, `leaderboardViewer` AND the
  // `board`/`period` they describe together from the SAME `{ board, period, results, viewer }` payload
  // -- one atomic update, never two actions or a second fetch, so the row list, the pinned "You" row
  // and the label they belong under can never come from different responses. All four move together
  // and are cleared together (or not at all) -- see `.failure` and `clearLeaderboard` below for the
  // other two transitions this group undergoes.
  .handleAction(actions.getLeaderboard.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.leaderboardResults = action.payload.results;
      nextState.leaderboardViewer = action.payload.viewer;
      nextState.leaderboardResultsBoard = action.payload.board;
      nextState.leaderboardResultsPeriod = action.payload.period;
      // L7: the SAME group, from the SAME response -- see actions.tsx's own comment on the widened Pick.
      nextState.leaderboardResultsWindow = action.payload.window;
      nextState.leaderboardResultsIsLoaded = true;
      nextState.leaderboardResultsLoadFailed = false;
    }),
  )
  .handleAction(actions.getLeaderboard.failure, (state) =>
    produce(state, (nextState) => {
      // Deliberately does not clear `leaderboardResults`, `leaderboardViewer`, or the board/period
      // they describe -- same rule as `summary`/`badges` above: stale data survives a failure that
      // follows an earlier success. L2: this group is one response (the handler above's own comment)
      // and stays CONSISTENT precisely by staying together -- clearing only `leaderboardViewer` here
      // would leave the stale podium/rows on screen paired with no viewer at all, which is the
      // inconsistency this group exists to avoid, not a fix for one. What keeps the retained group off
      // the screen under the WRONG label is not clearing it, but `leaderboardResultsBoard`/`Period`
      // surviving alongside it so `LeaderboardsContainer` can see the mismatch and render nothing.
      nextState.leaderboardResultsLoadFailed = true;
    }),
  )
  .handleAction(actions.getLeaderboardCrowns.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.leaderboardCrowns = action.payload;
      nextState.leaderboardCrownsIsLoaded = true;
      nextState.leaderboardCrownsLoadFailed = false;
    }),
  )
  .handleAction(actions.getLeaderboardCrowns.failure, (state) =>
    produce(state, (nextState) => {
      nextState.leaderboardCrownsLoadFailed = true;
    }),
  )
  .handleAction(actions.setLeaderboardBoard, (state, action) =>
    produce(state, (nextState) => {
      nextState.leaderboardBoard = action.payload;
    }),
  )
  .handleAction(actions.setLeaderboardPeriod, (state, action) =>
    produce(state, (nextState) => {
      nextState.leaderboardPeriod = action.payload;
    }),
  )
  .handleAction(actions.clearLeaderboard, (state) =>
    produce(state, (nextState) => {
      nextState.leaderboardResults = [];
      nextState.leaderboardResultsIsLoaded = false;
      nextState.leaderboardResultsLoadFailed = false;
      // L2: reset alongside leaderboardResults above -- same tab-unmount lifecycle, and the whole
      // group moves together (see the interface's own comment).
      nextState.leaderboardViewer = null;
      nextState.leaderboardResultsBoard = null;
      nextState.leaderboardResultsPeriod = null;
      // L7: reset alongside the rest of this group -- same tab-unmount lifecycle.
      nextState.leaderboardResultsWindow = null;
      nextState.leaderboardCrowns = [];
      nextState.leaderboardCrownsIsLoaded = false;
      nextState.leaderboardCrownsLoadFailed = false;
      nextState.leaderboardBoard = LEADERBOARD_BOARD.RECEIVED;
      // leaderboard-default-quarter: must stay in step with initialState's own leaderboardPeriod above
      // -- this is what the tab resets to on unmount, so a mismatch here would make the default silently
      // differ between first mount and a remount.
      nextState.leaderboardPeriod = LEADERBOARD_PERIOD.QUARTER;
      // overview-leaderboard-card-plan.md Part 2: deliberately NOT touched here -- this action is the
      // Leaderboard TAB's own reset (dispatched on that tab's unmount), and the Overview rail card's
      // data must survive it. `overviewLeaderboardResults`/`IsLoaded`/`LoadFailed` reset on
      // `clearSparks` instead (above), the same lifecycle as the rest of the Overview's own data.
    }),
  )
  // overview-leaderboard-card-plan.md Part 2: the Overview rail card's own pair -- the same two
  // handlers as `getLeaderboard`'s pair above, writing to the SEPARATE `overviewLeaderboardResults`
  // field. `.success` stores the whole `{ results, viewer }` payload as one object and clears a prior
  // failure; `.failure` sets ONLY the flag, leaving stale data on screen -- same "stale data survives
  // a failure" rule as every other trio in this file. It writes no board/period alongside them, unlike
  // `getLeaderboard.success` above: this card holds its own board/period in component state, so its
  // label cannot get ahead of its data (see that handler's own comment for what happens when it can).
  .handleAction(actions.getOverviewLeaderboard.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.overviewLeaderboardResults = action.payload;
      nextState.overviewLeaderboardIsLoaded = true;
      nextState.overviewLeaderboardLoadFailed = false;
    }),
  )
  .handleAction(actions.getOverviewLeaderboard.failure, (state) =>
    produce(state, (nextState) => {
      nextState.overviewLeaderboardLoadFailed = true;
    }),
  )
  // redeem-tab-plan.md PR2: mirrors the summary/badges/badgeDefinitions handlers above field-for-field
  // -- `.success` stores the whole tagged summary (counters + ledger together) and clears a prior
  // failure flag; `.failure` sets ONLY the flag, deliberately leaving stale data on screen rather than
  // blanking it. Reset by clearSparks above, alongside the rest of the logged-in user's own lifecycle.
  .handleAction(actions.getOwnRedemptionSummary.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.ownRedemptionSummary = action.payload;
      nextState.ownRedemptionSummaryIsLoaded = true;
      nextState.ownRedemptionSummaryLoadFailed = false;
    }),
  )
  .handleAction(actions.getOwnRedemptionSummary.failure, (state) =>
    produce(state, (nextState) => {
      nextState.ownRedemptionSummaryLoadFailed = true;
    }),
  )
  // phase-50-w6b: mirrors getRewardItems' pair exactly -- `.success` sets the data and clears a prior
  // failure flag; `.failure` sets ONLY the flag, deliberately leaving stale data on screen (same
  // "don't hide last-known-good numbers" rule as every other trio in this file).
  .handleAction(actions.getRedemptionStats.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.redemptionStats = action.payload;
      nextState.redemptionStatsIsLoaded = true;
      nextState.redemptionStatsLoadFailed = false;
    }),
  )
  .handleAction(actions.getRedemptionStats.failure, (state) =>
    produce(state, (nextState) => {
      nextState.redemptionStatsLoadFailed = true;
    }),
  )
  // 923-core-context context/engineering/spark-rewards/phase-52-spread-your-sparks.md §4 S3: mirrors getRewardItems' pair exactly -- `.success` sets the
  // data and clears a prior failure flag; `.failure` sets ONLY the flag, deliberately leaving
  // already-rendered cards on screen rather than clearing them (house rule: `.failure` never clears
  // data -- same "don't hide last-known-good data" rule as every other trio in this file).
  .handleAction(actions.getLeaderboardCollaborators.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.leaderboardCollaborators = action.payload;
      nextState.leaderboardCollaboratorsIsLoaded = true;
      nextState.leaderboardCollaboratorsLoadFailed = false;
    }),
  )
  .handleAction(actions.getLeaderboardCollaborators.failure, (state) =>
    produce(state, (nextState) => {
      nextState.leaderboardCollaboratorsLoadFailed = true;
    }),
  )
  // 923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md L8: mirrors getLeaderboardCollaborators' pair exactly -- `.success`
  // sets the data and clears a prior failure flag; `.failure` sets ONLY the flag, leaving stale data on
  // screen (same "don't hide last-known-good data" rule as every other trio in this file).
  .handleAction(actions.getAllTimeRank.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.allTimeRank = action.payload;
      nextState.allTimeRankLoadFailed = false;
    }),
  )
  .handleAction(actions.getAllTimeRank.failure, (state) =>
    produce(state, (nextState) => {
      nextState.allTimeRankLoadFailed = true;
    }),
  )
  .handleAction(actions.getAllTimeSummary.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.allTimeSummary = action.payload;
      nextState.allTimeSummaryLoadFailed = false;
    }),
  )
  .handleAction(actions.getAllTimeSummary.failure, (state) =>
    produce(state, (nextState) => {
      nextState.allTimeSummaryLoadFailed = true;
    }),
  )
  /**
   * 923-core-context context/engineering/spark-rewards/phase-55-person-sparks-drawer.md §6: opening the drawer for a NEW subject -- sets `userId` and
   * `loading`, and clears whatever `data`/`failed` a PREVIOUSLY open person left behind, so the drawer
   * never flashes the last person's counters under the new person's name while the new fetch is still
   * in flight.
   */
  .handleAction(actions.getPersonSparks.request, (state, action) =>
    produce(state, (nextState) => {
      nextState.personSparks.userId = action.payload;
      nextState.personSparks.loading = true;
      nextState.personSparks.data = null;
      nextState.personSparks.failed = false;
    }),
  )
  /**
   * 🚨 Stale-subject guard: a success is IGNORED unless `action.payload.userId` matches the slot's
   * CURRENT `userId` -- opening person A, then B, before A's four-endpoint fetch lands, must not let
   * A's late response overwrite B's drawer. Mirrors `UserRecognitionsContainer`'s own Phase 37 D2 guard,
   * applied here at the reducer rather than the component, since this slot (unlike `profileBadges`) has
   * its own `userId` field to compare against.
   */
  .handleAction(actions.getPersonSparks.success, (state, action) =>
    produce(state, (nextState) => {
      if (action.payload.userId !== nextState.personSparks.userId) {
        return;
      }
      nextState.personSparks.data = action.payload.data;
      nextState.personSparks.loading = false;
      nextState.personSparks.failed = false;
    }),
  )
  // Same stale-subject guard as `.success` above, for the same reason -- a failure for a person who is
  // no longer the open subject must not raise the CURRENT subject's error state.
  .handleAction(actions.getPersonSparks.failure, (state, action) =>
    produce(state, (nextState) => {
      if (action.payload.userId !== nextState.personSparks.userId) {
        return;
      }
      nextState.personSparks.loading = false;
      nextState.personSparks.failed = true;
    }),
  )
  // Dispatched on the drawer's close -- resets the whole slot, including `userId`, so a stale success/
  // failure arriving after close (guarded above) has nothing left to match against.
  .handleAction(actions.clearPersonSparks, (state) =>
    produce(state, (nextState) => {
      nextState.personSparks.userId = null;
      nextState.personSparks.data = null;
      nextState.personSparks.loading = false;
      nextState.personSparks.failed = false;
    }),
  );

export { reducer as SparksReducer };
